import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Modal, Form, Input, InputNumber, Select, Flex, Checkbox, Tooltip, Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import { getEnumList } from '@extensions/utils';

import { IBill } from '@entities/bill';
import { ICity } from '@entities/city';
import { ICountryItem } from '@entities/country-item';
import { ICountry } from '@entities/country';

import { Currency, enumSign as currencySign } from '@enums/currency';
import { UserBalanceType, enumLabel as balanceTypeLabel } from '@enums/user-balance-type';
import { IEnumItem } from '@enums/enum-item';

import { IPackageTemplate } from '@flp/entities/package-template';

import { PaymentType } from '@flp/enums/payment-type';

import { SumIcon } from '@icons/index';

import './bills.css';

dayjs.extend(utc);

export interface IBillParams {
    billIds: Array<string>;
    boxGroupId?: string;
    setPalletQtyRequired: boolean;
    palletQty?: number;
    setQtyRequired: boolean;
    qty?: number;
    truckPrice?: number;
    isTotalPriceManual: boolean;
    totalPrice: number;
    comment?: string;
    setCityRequired: boolean;
    cityId?: string;
    setVolumeRequired: boolean;
    volume?: number;
    setItemRequired: boolean;
    itemId?: string;
    setCountryRequired: boolean;
    countryId?: string;
    setBalanceTypeRequired?: boolean;
    userBalanceType: UserBalanceType;
}

interface IBillModalParams {
    bill: IBill;
    cities: Array<ICity>;
    items: Array<ICountryItem>;
    countries: Array<ICountry>;
    packages: Array<IPackageTemplate>;
    onSave: (value: IBillParams) => void;
    onCancel: () => void;
}

const BillModal = (props: IBillModalParams) => {
    const { bill, cities, items, countries, packages, onSave, onCancel } = props;

    const { TextArea } = Input;

    const [editBillForm] = Form.useForm();

    const setCityRequired = Form.useWatch('setCityRequired', editBillForm);
    const setPalletQtyRequired = Form.useWatch('setPalletQtyRequired', editBillForm);
    const setQtyRequired = Form.useWatch('setQtyRequired', editBillForm);
    const setItemRequired = Form.useWatch('setItemRequired', editBillForm);
    const setPackageRequired = Form.useWatch('setPackageRequired', editBillForm);
    const setCountryRequired = Form.useWatch('setCountryRequired', editBillForm);
    const setBalanceTypeRequired = Form.useWatch('setBalanceTypeRequired', editBillForm);
    const isTotalPriceManual = Form.useWatch('isTotalPriceManual', editBillForm);

    const [currentPackage, setCurrentPackage] = useState<IPackageTemplate>();
    const [balanceTypes] = useState<Array<IEnumItem>>(getEnumList(UserBalanceType, balanceTypeLabel));

    useEffect(() => {
        setCurrentPackage(packages.find((p) => p.id == bill.packageTemplateId));
    }, [packages]);

    useEffect(() => {
        editBillForm.setFieldsValue({
            id: bill?.id,
            countryId: bill?.countryId,
            itemId: bill?.itemId,
            boxGroupId: bill?.boxGroupId,
            palletQty: bill?.palletQty,
            qty: bill?.qty,
            cityId: bill?.cityId,
            volume: bill?.volume,
            truckPrice: bill?.truckPrice,
            truckPriceUsd: bill?.truckPriceUsd,
            totalPrice: bill?.totalPrice,
            isTotalPriceManual: bill?.isTotalPriceManual,
            comment: bill?.comment,
            packageTemplateId: bill?.packageTemplateId,
            userBalanceType: bill?.userBalanceType,
        });
    }, []);

    return (
        <Modal
            width={650}
            title={`Изменить счет для "${bill?.consigneeKeyName ?? bill?.userName}"`}
            open={true}
            okText='ОК'
            onOk={() => editBillForm.submit()}
            onCancel={() => {
                editBillForm.resetFields();
                onCancel();
            }}
        >
            <Form
                colon={false}
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
                onFinish={onSave}
                form={editBillForm}
                style={{ marginTop: 20 }}
            >
                <Form.Item name='id' hidden={true}>
                    <Input />
                </Form.Item>
                <Form.Item name='boxGroupId' hidden={true}>
                    <Input />
                </Form.Item>

                <Form.Item label='Страна' style={{ marginBottom: 0 }}>
                    <Flex gap='small'>
                        <Form.Item name='setCountryRequired' valuePropName='checked'>
                            <Checkbox />
                        </Form.Item>
                        <Form.Item
                            name='countryId'
                            rules={[{ required: setCountryRequired, message: 'Выберите страну' }]}
                            style={{ width: '100%' }}
                        >
                            <Select
                                disabled={!setCountryRequired}
                                allowClear
                                showSearch={{
                                    filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                    filterSort: (a, b) =>
                                        (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                                }}
                                options={countries.map((i) => {
                                    return { value: i.id, label: i.name };
                                })}
                            ></Select>
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item label='Продукт' style={{ marginBottom: 0 }}>
                    <Flex gap='small'>
                        <Form.Item name='setItemRequired' valuePropName='checked'>
                            <Checkbox />
                        </Form.Item>
                        <Form.Item
                            name='itemId'
                            rules={[{ required: setItemRequired, message: 'Выберите продукт' }]}
                            style={{ width: '100%' }}
                        >
                            <Select
                                disabled={!setItemRequired}
                                allowClear
                                showSearch={{
                                    filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                    filterSort: (a, b) =>
                                        (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                                }}
                                options={items.map((i) => {
                                    return { value: i.itemId, label: i.itemName };
                                })}
                            ></Select>
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item label='Упаковка / тип' style={{ marginBottom: 0 }}>
                    <Flex gap='large'>
                        <Flex gap='small'>
                            <Form.Item name='setPackageRequired' valuePropName='checked'>
                                <Checkbox />
                            </Form.Item>
                            <Form.Item
                                name={`packageTemplateId`}
                                rules={[{ required: setPackageRequired, message: 'Укажите упаковку' }]}
                                style={{ width: 215 }}
                            >
                                <Select
                                    disabled={!setPackageRequired}
                                    showSearch={{
                                        filterOption: (input, option) =>
                                            (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                        filterSort: (a, b) =>
                                            (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                                    }}
                                    options={packages.map((i) => {
                                        return { value: i.id, label: i.name };
                                    })}
                                    onChange={(value) => {
                                        const tmpPackage = packages.find((p) => p.id == value);
                                        setCurrentPackage(tmpPackage);
                                        if (!tmpPackage?.usePrice) editBillForm.setFieldValue('truckPriceUsd', undefined);

                                        if (tmpPackage && tmpPackage.qtyPerPallet) {
                                            const qty: number = editBillForm.getFieldValue('qty') ?? 0;
                                            if (qty <= 0) return;

                                            let palletQty: number = qty / tmpPackage.qtyPerPallet;
                                            editBillForm.setFieldValue('palletQty', palletQty);
                                            editBillForm.setFieldValue('setPalletQtyRequired', true);

                                            const truckPrice: number = editBillForm.getFieldValue('truckPrice') ?? 0;

                                            let totalPrice: number = 0;
                                            if (tmpPackage.paymentType == PaymentType.Item) {
                                                if (!!bill.packages?.length) {
                                                    bill.packages.map((p) => {
                                                        if (p.usePrice !== false) {
                                                            totalPrice += ((p.boxQty || 0) / p.qtyPerPallet) * truckPrice;
                                                        }
                                                    });
                                                } else {
                                                    totalPrice = qty * truckPrice;
                                                }
                                            } else {
                                                totalPrice = palletQty * truckPrice;
                                            }

                                            editBillForm.setFieldValue('totalPrice', totalPrice);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Flex>

                        <Form.Item label='Кол-во' style={{ marginBottom: 0 }}>
                            <Flex gap='small'>
                                <Form.Item name='setQtyRequired' valuePropName='checked'>
                                    <Checkbox />
                                </Form.Item>
                                <Form.Item>
                                    <Space.Compact>
                                        <Form.Item name='qty' noStyle>
                                            <InputNumber
                                                disabled={!setQtyRequired}
                                                min={0}
                                                onChange={(value: number | null) => {
                                                    const qty: number = value || 0;
                                                    editBillForm.setFieldValue('qty', qty);

                                                    if (!currentPackage?.qtyPerPallet) return;

                                                    let palletQty: number = qty / currentPackage.qtyPerPallet;
                                                    editBillForm.setFieldValue('palletQty', palletQty);
                                                    editBillForm.setFieldValue('setPalletQtyRequired', true);

                                                    const truckPrice: number = editBillForm.getFieldValue('truckPrice') ?? 0;

                                                    const totalPrice =
                                                        currentPackage.paymentType == PaymentType.Item
                                                            ? qty * truckPrice
                                                            : palletQty * truckPrice;
                                                    editBillForm.setFieldValue('totalPrice', +totalPrice);
                                                }}
                                            />
                                        </Form.Item>
                                        <Space.Addon>шт</Space.Addon>
                                    </Space.Compact>
                                </Form.Item>
                            </Flex>
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item label='Палета' style={{ marginBottom: 0 }}>
                    <Flex gap='large'>
                        <Flex gap='small'>
                            <Form.Item name='setPalletQtyRequired' valuePropName='checked'>
                                <Checkbox />
                            </Form.Item>
                            <Form.Item>
                                <Space.Compact>
                                    <Form.Item name='palletQty' noStyle>
                                        <InputNumber
                                            disabled={!setPalletQtyRequired}
                                            precision={3}
                                            decimalSeparator=','
                                            min={0}
                                            onChange={(value: number | null) => {
                                                const palletQty: number = value ?? 0;
                                                editBillForm.setFieldValue('palletQty', palletQty);

                                                if (!currentPackage?.qtyPerPallet) return;

                                                const qty: number = editBillForm.getFieldValue('qty') ?? 0;
                                                const truckPrice: number = editBillForm.getFieldValue('truckPrice') ?? 0;

                                                const totalPrice =
                                                    currentPackage.paymentType == PaymentType.Item
                                                        ? qty * truckPrice
                                                        : palletQty * truckPrice;

                                                editBillForm.setFieldValue('totalPrice', +totalPrice);
                                            }}
                                        />
                                    </Form.Item>
                                    <Space.Addon>шт</Space.Addon>
                                </Space.Compact>
                            </Form.Item>
                        </Flex>
                    </Flex>
                </Form.Item>

                <Form.Item label='Расчетный город' style={{ marginBottom: 0 }}>
                    <Flex gap='small'>
                        <Form.Item name='setCityRequired' valuePropName='checked'>
                            <Checkbox />
                        </Form.Item>
                        <Form.Item
                            name='cityId'
                            rules={[{ required: setCityRequired, message: 'Выберите город' }]}
                            style={{ width: '100%' }}
                        >
                            <Select
                                disabled={!setCityRequired}
                                allowClear
                                showSearch={{
                                    filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                    filterSort: (a, b) =>
                                        (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                                }}
                                options={cities.map((c) => {
                                    return { value: c.id, label: c.name };
                                })}
                            ></Select>
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item label='Тип расчета' style={{ marginBottom: 0 }}>
                    <Flex gap='small'>
                        <Form.Item name='setBalanceTypeRequired' valuePropName='checked'>
                            <Checkbox />
                        </Form.Item>
                        <Form.Item name='userBalanceType' style={{ width: '100%' }}>
                            <Select
                                style={{ width: 200 }}
                                disabled={!setBalanceTypeRequired}
                                options={balanceTypes.map((s) => {
                                    return { value: s.value, label: s.label };
                                })}
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item label='Тариф' style={{ marginBottom: 0 }}>
                    <Flex gap='middle'>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space.Compact>
                                <Form.Item name='truckPrice' noStyle>
                                    <InputNumber
                                        disabled={isTotalPriceManual}
                                        decimalSeparator=','
                                        min={0}
                                        step='0.1'
                                        stringMode
                                        precision={2}
                                        onChange={(value: number | null) => {
                                            const truckPrice: number = value || 0;
                                            editBillForm.setFieldValue('truckPrice', truckPrice);

                                            if (!currentPackage?.qtyPerPallet) return;

                                            let qty: number = editBillForm.getFieldValue('qty');
                                            let palletQty: number = editBillForm.getFieldValue('palletQty');

                                            let totalPrice: number = 0;
                                            if (currentPackage.paymentType == PaymentType.Item) {
                                                if (!!bill.packages?.length) {
                                                    bill.packages.map((p) => {
                                                        if (p.usePrice !== false) {
                                                            totalPrice += ((p.boxQty || 0) / p.qtyPerPallet) * truckPrice;
                                                        }
                                                    });
                                                } else {
                                                    totalPrice = qty * truckPrice;
                                                }
                                            } else {
                                                totalPrice = palletQty * truckPrice;
                                            }

                                            editBillForm.setFieldValue('totalPrice', totalPrice);
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>
                                    <Tooltip title='Транспортная доставка' placement='topRight'>
                                        <div>{currencySign(Currency.Eur)}</div>
                                    </Tooltip>
                                </Space.Addon>
                            </Space.Compact>
                        </Form.Item>
                        {currentPackage?.useUsd && (
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space.Compact>
                                    <Form.Item name='truckPriceUsd' noStyle>
                                        <InputNumber
                                            decimalSeparator=','
                                            min={0}
                                            precision={2}
                                            onChange={(value: number | null) => {
                                                const truckPrice: number = value || 0;
                                                editBillForm.setFieldValue('truckPriceUsd', truckPrice);
                                            }}
                                        />
                                    </Form.Item>
                                    <Space.Addon>
                                        <Tooltip title='Транспортная доставка' placement='topRight'>
                                            <div>{currencySign(Currency.Usd)}</div>
                                        </Tooltip>
                                    </Space.Addon>
                                </Space.Compact>
                            </Form.Item>
                        )}
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Flex gap='small'>
                                <Tooltip title='Задать итоговую сумму вручную'>
                                    <Form.Item name='isTotalPriceManual' valuePropName='checked'>
                                        <Checkbox
                                            onChange={(value: CheckboxChangeEvent) => {
                                                const isManual: boolean = value.target.checked;

                                                if (!isManual) {
                                                    const truckPrice: number = editBillForm.getFieldValue('truckPrice') || 0;

                                                    let totalPrice: number = 0;
                                                    if (currentPackage?.paymentType == PaymentType.Item) {
                                                        if (!!bill.packages?.length) {
                                                            bill.packages.map((p) => {
                                                                if (p.usePrice !== false) {
                                                                    totalPrice += ((p.boxQty || 0) / p.qtyPerPallet) * truckPrice;
                                                                }
                                                            });
                                                        } else {
                                                            totalPrice = editBillForm.getFieldValue('qty') * truckPrice;
                                                        }
                                                    } else {
                                                        totalPrice = editBillForm.getFieldValue('palletQty') * truckPrice;
                                                    }

                                                    editBillForm.setFieldValue('totalPrice', totalPrice);
                                                }

                                                editBillForm.setFieldValue('isTotalPriceManual', isManual);
                                            }}
                                        />
                                    </Form.Item>
                                </Tooltip>

                                <Form.Item>
                                    <Space.Compact>
                                        <Form.Item
                                            noStyle
                                            name='totalPrice'
                                            rules={[{ required: isTotalPriceManual, message: 'Укажите итоговую сумму' }]}
                                        >
                                            <InputNumber decimalSeparator=',' min={0} precision={2} disabled={!isTotalPriceManual} />
                                        </Form.Item>
                                        <Space.Addon>
                                            <SumIcon />
                                        </Space.Addon>
                                    </Space.Compact>
                                </Form.Item>
                            </Flex>
                        </Form.Item>
                    </Flex>
                </Form.Item>
                <Form.Item
                    label='Комментарий'
                    name='comment'
                    rules={[{ required: isTotalPriceManual, message: 'Укажите причину ввода суммы вручную' }]}
                >
                    <TextArea rows={4} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BillModal;
